import { useEffect, type ReactNode, type ButtonHTMLAttributes } from 'react'

export const inputCls = 'w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm focus:border-orange outline-none disabled:opacity-50'
export const labelCls = 'block text-[11px] uppercase tracking-widest text-acier mb-1'

const variants = {
  primary: 'bg-orange hover:bg-orange-hover text-blanc',
  ghost: 'bg-white/5 hover:bg-white/10 border border-white/10 text-blanc',
  danger: 'bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300',
  link: 'text-orange hover:underline px-0 py-0',
}
export function Btn({ variant = 'primary', className = '', small, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof variants; small?: boolean }) {
  return <button {...p} className={`${variant === 'link' ? '' : small ? 'px-3 py-1.5' : 'px-5 py-2.5'} rounded-sm text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]} ${className}`} />
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return <div className={className}><label className={labelCls}>{label}</label>{children}</div>
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', k); document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = '' }
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={onClose}>
      <div className={`w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'} bg-noir border border-white/15 rounded shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="font-display text-2xl tracking-wide">{title}</h2>
          <button onClick={onClose} aria-label="Fermer" className="text-acier hover:text-orange text-2xl leading-none">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

export function Card({ title, actions, children, className = '' }: { title?: string; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`border border-white/10 rounded bg-white/[0.02] ${className}`}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-white/10">
          {title && <h2 className="font-display text-xl tracking-wide">{title}</h2>}
          <div className="flex gap-2">{actions}</div>
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) { return <p className="text-acier text-sm py-2">{children}</p> }

export function Msg({ msg }: { msg: string }) {
  if (!msg) return null
  const err = /^erreur/i.test(msg)
  return <p className={`text-xs mt-3 break-words ${err ? 'text-red-300' : 'text-green-300'}`}>{msg}</p>
}

export function Stat({ label, value, sub, href }: { label: string; value: string | number; sub?: string; href?: string }) {
  const body = (
    <div className="border border-white/10 rounded p-5 bg-white/[0.02] hover:border-orange/50 transition-colors h-full">
      <div className="font-display text-4xl text-orange">{value}</div>
      <div className="text-xs uppercase tracking-widest text-acier mt-1">{label}</div>
      {sub && <div className="text-xs text-acier mt-1">{sub}</div>}
    </div>
  )
  return href ? <a href={href}>{body}</a> : body
}

export const money = (n: number | string | null | undefined) => (Number(n) || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })
export const fmtDate = (d: string | null | undefined) => d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
export const fmtDateTime = (d: string | null | undefined) => d ? new Date(d).toLocaleString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
export const today = () => new Date().toISOString().slice(0, 10)
export const addDays = (d: string, n: number) => { const x = new Date(d + 'T12:00:00'); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10) }
